import { useMemo, useState } from 'react';
import { GlobalPaywallToggle } from './GlobalPaywallToggle';
import { FeatureFlags } from './FeatureFlags';
import { StripeInfo, useEnvironmentSettings } from '../../queries/settings';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';

export function SystemSettingsPage() {
  return (
    <div className="space-y-8">
      <PageHeader title="Settings" description="Site controls and a read-only view of this environment's configuration." />

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Global paywall</h2>
        <GlobalPaywallToggle />
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Feature flags</h2>
          <p className="text-sm text-muted-foreground">
            Turn a feature on or off for everyone at once. Members given their own setting on their user page keep it.
          </p>
        </div>
        <FeatureFlags />
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Environment</h2>
        <EnvironmentInspector />
      </section>
    </div>
  );
}

function StripeBadge({ info }: { info: StripeInfo }) {
  const isLive = info.accountType === 'Live Account';
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <CardTitle>Stripe account</CardTitle>
        <Badge variant={isLive ? 'destructive' : 'warning'}>{info.accountType}</Badge>
      </CardHeader>
      <CardContent className="pt-0">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">Environment</dt>
          <dd>{info.environment}</dd>
          {info.accountId && (
            <>
              <dt className="text-muted-foreground">Account id</dt>
              <dd className="font-mono text-xs">{info.accountId}</dd>
            </>
          )}
          <dt className="text-muted-foreground">Webhook secret</dt>
          <dd>{info.hasWebhookSecret ? 'Configured' : 'Missing'}</dd>
          <dt className="text-muted-foreground">Publishable key</dt>
          <dd>{info.hasPublishableKey ? 'Configured' : 'Missing'}</dd>
        </dl>
      </CardContent>
    </Card>
  );
}

function EnvironmentInspector() {
  const { data, isLoading } = useEnvironmentSettings();
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const vars = data?.envVars ?? [];
    return term ? vars.filter((v) => v.key.toLowerCase().includes(term)) : vars;
  }, [data, search]);

  if (isLoading || !data) {
    return <Spinner />;
  }

  return (
    <div className="space-y-4">
      {data.stripeInfo && <StripeBadge info={data.stripeInfo} />}

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter variables…"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <span className="text-xs text-muted-foreground">
          {data.count} variables · {data.sensitiveCount} sensitive (masked)
        </span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="bg-muted/50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Key</th>
              <th className="bg-muted/50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visible.map((v) => (
              <tr key={v.key} className="transition-colors hover:bg-muted/40">
                <td className="px-3 py-2.5 font-mono text-xs">{v.key}</td>
                <td className={`px-3 py-2.5 font-mono text-xs ${v.isSensitive ? 'text-muted-foreground' : ''}`}>{v.value || '—'}</td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={2} className="px-3 py-10 text-center text-muted-foreground">No variables match the filter.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
